<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import { useMainStore } from '@/stores/main'
import Loading from '@/components/Loading.vue'
import PageHeader from '@/components/PageHeader.vue'
import UserName from '@/components/UserName.vue'
import TrackTitle from '@/components/TrackTitle.vue'
import { podiumClass } from '@/lib/podium'
import { formatGap } from '@/lib/times'
import { useTrackTitle } from '@/composables/useTrackTitle'

const route = useRoute()
const { t } = useI18n()
const store = useMainStore()
const { primaryName } = useTrackTitle()

const trackId = computed(() => route.params.trackId)
const carId = computed(() => route.params.carId ?? null)
const track = computed(() => store.tracks.find(tr => tr.id === trackId.value))
const car = computed(() => carId.value ? store.cars.find(c => c.id === carId.value) : null)

const breadcrumbs = computed(() => {
  const items = [
    { title: t('leaderboard.title'), to: { name: 'leaderboard' } },
    {
      title: track.value ? primaryName(track.value.id) : trackId.value,
      ...(carId.value ? { to: { name: 'leaderboard_track', params: { trackId: trackId.value } } } : {}),
    },
  ]
  if (carId.value) {
    items.push({ title: car.value ? t(`cars.${car.value.id}`) : carId.value })
  }
  return items
})

/** Title of the page: the car when one is chosen, the track otherwise. */
const headerTitle = computed(() => {
  if (carId.value) return car.value ? t(`cars.${car.value.id}`) : carId.value
  return track.value ? primaryName(track.value.id) : trackId.value
})

const entries = ref(null)
const error = ref(false)

/**
 * Rows of the times asked for at once: the endpoint is paged (see LeaderboardController::byTrack),
 * and this page shows the list in a single table of its own.
 */
const TRACK_LIMIT = 50

const medalIcon = (position) => {
  if (position <= 3) return 'mdi-medal'
  return 'mdi-pound'
}

const fetchEntries = () => {
  entries.value = null
  error.value = false
  const url = carId.value
    ? Config.server.routes.api.leaderboard_track_car(trackId.value, carId.value)
    : Config.server.routes.api.leaderboard_track(trackId.value, 1, TRACK_LIMIT)
  Server.get(url)
    .then(response => {
      // Per car the answer is still a plain list; per track it is a page of one.
      entries.value = carId.value ? response.data : response.data?.data?.entries ?? []
    })
    .catch(() => {
      error.value = true
      entries.value = []
    })
}

onMounted(fetchEntries)
watch([trackId, carId], fetchEntries)
</script>

<template>
  <main>
    <v-container>
      <!-- Breadcrumb -->
      <v-breadcrumbs
        :items="breadcrumbs"
        class="px-0 pb-2"
      >
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>

      <!-- Header -->
      <PageHeader
        :title="headerTitle"
        :icon="carId ? 'mdi-car-sports' : 'mdi-podium-gold'"
      >
        <template v-if="!carId && track" #title>
          <TrackTitle :track-id="track.id" variant="stacked" />
        </template>
      </PageHeader>
      <v-row v-if="carId && track" class="mb-4">
        <v-col>
          <v-chip size="small" variant="tonal" color="primary" prepend-icon="mdi-flag-checkered">
            <TrackTitle :track-id="track.id" :flag="false" />
          </v-chip>
        </v-col>
      </v-row>

      <!-- Car filter/switch chips -->
      <v-row class="mb-4">
        <v-col cols="12">
          <p class="text-caption text-medium-emphasis mb-2">{{ carId ? $t('leaderboard.switchCar') : $t('leaderboard.filterByCar') }}</p>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="c in store.cars"
              :key="c.id"
              :to="c.id === carId
                ? { name: 'leaderboard_track', params: { trackId } }
                : { name: 'leaderboard_track_car', params: { trackId, carId: c.id } }"
              :color="(!carId || c.id === carId) ? 'primary' : 'default'"
              :variant="c.id === carId ? 'flat' : 'outlined'"
              size="small"
              prepend-icon="mdi-car-sports"
              link
            >
              {{ $t(`cars.${c.id}`) }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <v-alert
        type="warning"
        variant="tonal"
        class="mb-4"
        :text="$t('leaderboard.developmentWarning')"
      />

      <!-- Loading -->
      <Loading v-if="entries === null" />

      <!-- Error -->
      <v-alert
        v-else-if="error"
        type="error"
        variant="tonal"
        :text="$t('leaderboard.loadError')"
        class="mb-4"
      />

      <!-- Empty -->
      <v-card v-else-if="entries.length === 0" elevation="0" variant="outlined" class="text-center pa-8">
        <v-icon size="64" color="medium-emphasis" class="mb-4">mdi-timer-off-outline</v-icon>
        <p class="text-h6 text-medium-emphasis">{{ $t('leaderboard.noTimes') }}</p>
      </v-card>

      <!-- Leaderboard table -->
      <v-card v-else elevation="2">
        <v-table hover>
          <thead>
            <tr>
              <th class="text-center" style="width:60px">#</th>
              <th>{{ $t('leaderboard.driver') }}</th>
              <th v-if="!carId">{{ $t('leaderboard.car') }}</th>
              <th class="text-right">{{ $t('leaderboard.lapTime') }}</th>
              <th class="text-right">{{ $t('standings.gap') }}</th>
              <th class="text-right">{{ $t('leaderboard.date') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(entry, index) in entries"
              :key="index"
              :class="{ 'top-entry': index < 3 }"
            >
              <td class="text-center">
                <v-icon
                  :class="podiumClass(index + 1)"
                  :icon="medalIcon(index + 1)"
                  size="20"
                />
                <span v-if="index >= 3" class="text-medium-emphasis text-caption ml-1">{{ index + 1 }}</span>
              </td>
              <td>
                <div class="d-flex align-center ga-2">
                  <UserName :name="entry.driver_name" :crest="entry.crest" class="font-weight-medium" />
                </div>
              </td>
              <td v-if="!carId">
                <v-chip size="small" variant="tonal" prepend-icon="mdi-car-sports">
                  {{ $t(`cars.${entry.car_id}`) }}
                </v-chip>
              </td>
              <td class="text-right">
                <span class="font-weight-bold text-mono" :class="index === 0 ? 'text-primary' : ''">
                  {{ entry.lap_time }}
                </span>
              </td>
              <td class="text-right text-mono text-medium-emphasis">
                {{ formatGap(entry.gap_ms) ?? '' }}
              </td>
              <td class="text-right text-medium-emphasis text-caption">
                {{ entry.date }}
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card>
    </v-container>
  </main>
</template>

<style scoped>
.text-mono {
  font-family: monospace;
  font-size: 1rem;
}
.top-entry td {
  background-color: rgba(var(--v-theme-primary), 0.04);
}
</style>
